import React from 'react'
import { Handle, Position } from 'reactflow'
import { useActivities, useCurrentActivity } from '../store'
import TextAvatar from '../components/TextAvatar'
import { Box, IconButton, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNavigate } from 'react-router-dom'
import ConfirmationDialog from '../components/ConfirmationDialog'
import { useState } from 'react'
import { workflowService } from '../../../../services/admin/workflow'
import { useAuthContext } from '../../../../hooks/useAuthContext'
import { colors } from '../../../../theme/colors'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import moment from 'moment'
import { formatUserFullName } from '../../../../utility/userName'

const Activity = ({ data }) => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const setCurrentActivity = useCurrentActivity((state) => state.setCurrentActivity)
  const setActivities = useActivities((state) => state.setActivities)
  const activities = useActivities((state) => state.activities)
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)

  const handleClick = () => {
    setCurrentActivity(data?.activity)
  }

  const deleteActivity = async (templateId) => {
    await workflowService.deleteActivityTemplate(user, templateId)

    const updatedActivities = activities?.filter((it) => it.id !== templateId)
    setActivities(updatedActivities)
  }

  // Format the title with version
  const getActivityTitle = () => {
    const activityName = data?.activity?.activity_template_name || ''
    const version = data?.version

    if (version) {
      return `${activityName} (v${version})`
    }
    return activityName
  }

  // Format the created date
  const formatCreatedDate = () => {
    if (data?.created_at) {
      return moment(new Date(data.created_at)).format('DD MMM YYYY, hh:mm A')
    }
    return null
  }

  const { upcomingUserAssignment, showUpcomingUser } = data

  return (
    <>
      <Handle type="target" position={Position.Left} />
      <Box
        onClick={handleClick}
        style={{
          maxWidth: 300,
          padding: 20,
          backgroundColor: 'white',
          borderRadius: 8,
          border: '0.5px solid #D3D3D3',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '2px 2px 6px gray',
        }}
        className="activity-node"
      >
        <Box style={{ display: 'flex' }}>
          <TextAvatar
            name={data?.activity?.activity_template_name}
            color={data?.activity?.color_code}
          />
          <Box style={{ marginLeft: 12, flex: 1 }}>
            <Typography variant="p" style={{ margin: 0, fontWeight: 'bold', color: '#27104e' }}>
              {getActivityTitle()}
            </Typography>

            {/* Display activity type - only in the workflow editor, never in ProcessFlowViewer */}
            {!data?.readOnly && data?.activity?.activity_type && (
              <Typography
                variant="p"
                style={{
                  display: 'block',
                  marginTop: 6,
                  marginLeft: 2,
                  fontSize: '14px',
                  color: '#231f2b',
                  fontWeight: '600',
                }}
              >
                ({data.activity.activity_type})
              </Typography>
            )}

            {/* Display assignee name */}
            {data?.first_name && data?.last_name && (
              <Box
                style={{
                  marginTop: 8,
                  fontSize: '16px',
                  color: '#656d92',
                  fontWeight: '600',
                }}
              >
                {formatUserFullName(data)}
              </Box>
            )}

            {/* Display created date for current activity */}
            {data?.isCurrentPosition && formatCreatedDate() && (
              <Box
                style={{
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '10px',
                  fontWeight: '600',
                }}
              >
                <CalendarMonthOutlinedIcon color="info" sx={{ fontSize: '14px' }} />
                <span style={{ color: '#656d92' }}>{formatCreatedDate()}</span>
              </Box>
            )}

            {/* Show UPCOMING user assignment for future activities */}
            {showUpcomingUser && (
              <Box
                className="upcoming-user"
                style={{
                  marginTop: 8,
                  fontSize: '16px',
                  color: '#656d92',
                  fontWeight: '600',
                }}
              >
                {upcomingUserAssignment?.userName || 'TBD'}
              </Box>
            )}

            <Box style={{ display: 'flex', columnGap: 12, marginTop: 12 }}>
              <IconButton
                style={{ backgroundColor: colors.iconButton }}
                size="small"
                onClick={() => navigate('/admin/activity-config/' + data?.activity?.id)}
              >
                <EditIcon sx={{ fontSize: '18px' }} />
              </IconButton>
              <IconButton
                style={{ backgroundColor: colors.iconButton }}
                size="small"
                onClick={() => setOpenConfirmDelete(true)}
              >
                <DeleteIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
      <ConfirmationDialog
        open={openConfirmDelete}
        title={`Delete activity '${data?.activity?.activity_template_name}'`}
        message={`Be careful! It will delete complete activity including all pages, fields and all other settings.`}
        onConfirm={() => {
          deleteActivity(data?.activity?.id)
        }}
        onClose={() => setOpenConfirmDelete(false)}
      />
      <Handle type="source" position={Position.Right} />
    </>
  )
}

export default Activity
